// The Hebrew text of a failed alert save. The Gateway answers a refused tree with 400 INVALID_TREE and the
// reasons in `data`: the schema's text (English) or the engine's, behind "engine publish rejected the tree: ".
// A server error (5xx) means the engine or the database failed, not that the tree is wrong.

import { CONNECTION_REFUSAL_TEXT } from './connectionRefusal'

type Failure = {
  response?: {
    status?: number
    data?: { error?: string; data?: unknown; message?: string }
  }
}

const MAX_REASONS = 4

const ENGINE_PREFIX = 'engine publish rejected the tree: '

// Engine refusals, from event.alerts_engine_publish_version. `$1` is the offending value or number.
const ENGINE_REASONS: Array<[RegExp, (value: string) => string]> = [
  [
    /^duplicate condition id in tree: (.*)$/,
    () => CONNECTION_REFUSAL_TEXT['second-parent'],
  ],
  [
    /^alert tree condition kind is not available for publish: (.*)$/,
    v => `סוג התנאי "${v}" עדיין לא זמין לפרסום`,
  ],
  [
    /^alert tree attribute is not registered for publish: (.*)$/,
    v => `המאפיין "${v}" אינו רשום במערכת`,
  ],
  [
    /^alert tree hierarchy role is not supported for publish: (.*)$/,
    v => `תפקיד בהיררכיה ("${v}") אינו נתמך`,
  ],
  [
    /^alert tree device_alert code is not registered for publish: (.*)$/,
    v => `קוד התראת המד "${v}" אינו רשום`,
  ],
  [
    /^alert tree condition names a period that is not available for publish: (.*)$/,
    v => `התקופה "${v}" עדיין לא זמינה לפרסום`,
  ],
  [
    /^alert tree hourly consumption parameter is not supported for publish: (.*)$/,
    v => `הפרמטר "${v}" אינו נתמך בצריכה שעתית`,
  ],
  [
    /^alert tree hourly consumption does not support the shared consumption type \(condition (.*)\)$/,
    v => `צריכה שעתית אינה תומכת בצריכה משותפת (תנאי ${v})`,
  ],
  [
    /^alert tree streak parameter is not supported for publish: (.*)$/,
    v => `הפרמטר "${v}" אינו נתמך ברצף`,
  ],
  [
    /^alert tree notification action has no message: action_no (.*)$/,
    v => `לפעולת שליחה ${v} אין טקסט הודעה`,
  ],
  [
    /^alert tree notification action has no channel: action_no (.*)$/,
    v => `לפעולת שליחה ${v} אין ערוץ שליחה`,
  ],
  [
    /^alert tree notification message contains malformed \{\{\.\.\.\}\} template braces: (.*)$/,
    v => `בהודעה יש סוגריים מסולסלים שגויים: ${v}`,
  ],
  [
    /^alert tree notification message token is not a registered template variable: (.*)$/,
    v => `בהודעה יש משתנה שאינו רשום: ${v}`,
  ],
  [
    /^alert tree notification message token is registered but not allowed: (.*)$/,
    v => `בהודעה יש משתנה שאינו מורשה: ${v}`,
  ],
  [
    /^compiled alert tree references condition kind without available preparer: (.*)$/,
    v => `למנוע אין עדיין הכנה לסוג התנאי "${v}"`,
  ],
  [/^alert tree calendar is malformed: /, () => 'לוח הזמנים של העץ אינו תקין'],
  [
    /^alert tree condition calendar is malformed: condition (.*)$/,
    v => `לוח הזמנים של תנאי מספר ${v} בעץ אינו תקין`,
  ],
  [
    /^alert tree calendar is not supported on this condition kind \(it judges no period, or it is a streak, which follows its source\): (.*)$/,
    v =>
      `לוח זמנים אינו נתמך בתנאי מסוג "${v}": התנאי לא בודק תקופה, או שהוא רצף שפועל לפי המקור שלו`,
  ],
  [
    /^alert tree calendar hours need an hour period: condition (.*)$/,
    v =>
      `לוח הזמנים של תנאי מספר ${v} בעץ כולל שעות, ובדיקה יומית לא יכולה להשתמש בהן`,
  ],
  [
    /^alert tree calendar has no condition to apply to: /,
    () => 'לוח הזמנים של העץ לא חל על אף בדיקת צריכה',
  ],
  [
    /^(?:temporal|streak) condition (.*) has empty source path$/,
    v => `לתנאי ${v} אין מקור נתונים`,
  ],
]

// The schema's own text, which names a path. The path stays as the Gateway wrote it.
const SCHEMA_REASONS: Array<[RegExp, (path: string, value: string) => string]> =
  [
    [
      /^(.*): required property '(.*)' not found$/,
      (path, v) => `חסר שדה חובה "${v}" ב־${path}`,
    ],
    [
      /^(.*): property '(.*)' is not evaluated and the schema does not allow unevaluated properties$/,
      (path, v) => `השדה "${v}" אינו מותר ב־${path}`,
    ],
    [
      /^(.*): must be the constant value '(.*)'$/,
      (path, v) => `הערך ב־${path} חייב להיות "${v}"`,
    ],
    [
      /^(.*): must be one of (.*)$/,
      (path, v) => `הערך ב־${path} חייב להיות אחד מ: ${v}`,
    ],
  ]

function reasonInHebrew(reason: string): string {
  const text = reason.startsWith(ENGINE_PREFIX)
    ? reason.slice(ENGINE_PREFIX.length)
    : reason
  for (const [pattern, say] of ENGINE_REASONS) {
    const match = pattern.exec(text)
    if (match) return say(match[1])
  }
  for (const [pattern, say] of SCHEMA_REASONS) {
    const match = pattern.exec(text)
    if (match) return say(match[1], match[2])
  }
  console.error('[saveError] unmapped engine reason:', text)
  return 'שגיאה טכנית'
}

export function describeSaveError(error: unknown): string {
  const failure = error as Failure | null
  const response = failure?.response
  if (response === undefined) {
    return (error as { isAxiosError?: boolean } | null)?.isAxiosError
      ? 'שמירה נכשלה — אין חיבור לשרת, נסו שוב'
      : 'שמירה נכשלה — אירעה שגיאה בלתי צפויה'
  }
  const status = response.status ?? 0
  const body = response.data
  if (status >= 500) {
    return 'שמירה נכשלה — תקלה בשרת ההתראות. העץ לא נפסל, נסו שוב בעוד רגע'
  }
  if (body?.error === 'ALERT_TYPE_NAME_CONFLICT') {
    return 'שמירה נכשלה — כבר קיימת התראה בשם הזה'
  }
  if (body?.error === 'INVALID_TREE' && Array.isArray(body.data)) {
    const reasons = body.data
      .filter((r): r is string => typeof r === 'string')
      .slice(0, MAX_REASONS)
      .map(reasonInHebrew)
    if (reasons.length > 0) return `לא נשמר — ${reasons.join(' • ')}`
  }
  return `שמירה נכשלה (קוד ${status})`
}
